import { useEffect, useRef, useState } from "react";

import { Spotlight, SpotlightCard } from "@atlaskit/onboarding";

import { FIRST_ISSUE_ID, Steps } from "../constants/ui";
import { getSpotlightMessagesForStep } from "../helpers";
import { useInteractiveBoardStore } from "../state";

import { adsFont, spotlightHeader } from "./spotlight-manager.styles";
import { useSpotlightManager } from "./spotlight-provider";

type SpotlightProps = React.ComponentProps<typeof Spotlight> & {
    description: string;
    key: string;
    step: Steps;
    topOffset?: number;
    leftOffset?: number;
};
export const SpotlightManager = () => {
    const [dimensions, setDimensions] = useState({
        height: typeof window !== "undefined" && window.innerHeight,
        width: typeof window !== "undefined" && window.innerWidth
    });
    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth
            });
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const [
        { isDemoMessageVisible, messages, step },
        { setSpotlightVisibility }
    ] = useInteractiveBoardStore();

    const spotlightCardRef = useRef<HTMLDivElement | null>(null);

    const [spotlightPosition, setSpotlightPosition] = useState<{
        top: string | undefined;
        left: string | undefined;
    }>({
        top: undefined,
        left: undefined
    });

    const [spotlight, setSpotlight] = useState<SpotlightProps | undefined>(
        undefined
    );

    const { targets, setCurrent } = useSpotlightManager();

    useEffect(() => {
        const spotlightMessages = getSpotlightMessagesForStep(step, messages);

        // make sure spotlight is visible again if a new step is encountered.
        void setSpotlightVisibility(true);

        switch (step) {
            case Steps.CREATE_FIRST_STORY:
                setSpotlight({
                    step: Steps.CREATE_FIRST_STORY,
                    target: "openIssueModalButton",
                    key: "openIssueModalButton",
                    leftOffset: -61,
                    topOffset: 10,
                    ...spotlightMessages
                });
                break;
            case Steps.SELECT_ISSUE_TYPE:
                setSpotlight({
                    step: Steps.SELECT_ISSUE_TYPE,
                    target: "issueType",
                    key: "issueType",
                    leftOffset: -61,
                    ...spotlightMessages
                });
                break;
            case Steps.ADD_SUMMARY:
                setSpotlight({
                    step: Steps.ADD_SUMMARY,
                    target: "summary",
                    key: "summary",
                    ...spotlightMessages,
                    leftOffset: -61
                });
                break;
            case Steps.ADD_DESCRIPTION:
                setSpotlight({
                    step: Steps.ADD_DESCRIPTION,
                    target: "issueDescription",
                    key: "issueDescription",
                    leftOffset: -61,
                    ...spotlightMessages
                });
                break;
            case Steps.SELECT_PRIORITY:
                setSpotlight({
                    step: Steps.SELECT_PRIORITY,
                    target: "priority",
                    key: "priority",
                    leftOffset: -61,
                    ...spotlightMessages
                });
                break;
            case Steps.SELECT_EPIC_LINK:
                setSpotlight({
                    step: Steps.SELECT_EPIC_LINK,
                    target: "epicLink",
                    key: "epicLink",
                    leftOffset: -61,
                    ...spotlightMessages
                });
                break;
            case Steps.MOVE_ISSUE:
                setSpotlight({
                    step: Steps.MOVE_ISSUE,
                    target: "create",
                    key: "create",
                    leftOffset: -369,
                    topOffset: -163,
                    ...spotlightMessages
                });
                break;
            case Steps.PULSE_CARD:
                void setSpotlightVisibility(false);
                setSpotlight({
                    step: Steps.PULSE_CARD,
                    description: "",
                    target: FIRST_ISSUE_ID,
                    key: FIRST_ISSUE_ID
                });
                break;
            default:
                console.warn("this step does not have a defined spotlight");
                void setSpotlightVisibility(false);
                setSpotlight(undefined);
        }
    }, [step, messages]);

    useEffect(() => {
        if (spotlight && targets) {
            const elementRef = targets.get(spotlight.target ?? "");
            if (elementRef?.current) {
                setCurrent && setCurrent(elementRef); // set current ref
            }

            // set card relative to current step element
            if (elementRef?.current && spotlightCardRef.current) {
                // additional padding to allow custom offset relative to target element
                const {
                    topOffset: additionalTopOffset = 0,
                    leftOffset: additionalLeftOffset = 0
                } = spotlight;

                const paddingTop = 10; // represents padding above spotlight relative to the bottom of the target element
                const paddingLeft = 60; // represents padding to the left of the spotlight.

                const {
                    offsetLeft: targetElementLeftOffset, // the target elements current position from the left relative to it's parent.
                    offsetTop: targetElementTopOffset, // the target elements current position from the top relative to it's parent.
                    offsetHeight: targetElementHeight // the target elements height
                } = elementRef.current;

                const spotlightTopOffset =
                    targetElementTopOffset +
                    targetElementHeight +
                    additionalTopOffset +
                    paddingTop +
                    "px";

                const spotlightLeftOffset =
                    targetElementLeftOffset +
                    additionalLeftOffset +
                    paddingLeft +
                    "px";

                setSpotlightPosition({
                    top: spotlightTopOffset,
                    left: spotlightLeftOffset
                });
            }
        } else {
            setCurrent && setCurrent(null);
        }
    }, [spotlight, dimensions]);

    if (!isDemoMessageVisible) {
        return;
    }

    return (
        <div
            style={{
                position: "absolute",
                top:
                    spotlightPosition.top !== undefined
                        ? spotlightPosition.top
                        : "159px", //this prevents a flash of the spotlight before it's positioned correctly.
                left:
                    spotlightPosition.left !== undefined
                        ? spotlightPosition.left
                        : "400px", //this prevents a flash of the spotlight before it's positioned correctly.
                zIndex: 1
            }}
        >
            {spotlight && (
                <SpotlightCard
                    ref={spotlightCardRef}
                    width={301}
                    actionsBeforeElement={
                        <div css={adsFont}>{`${spotlight.step + 1}/7`}</div>
                    }
                    key={spotlight.key}
                >
                    <div css={spotlightHeader}>{spotlight.heading}</div>
                    <div css={adsFont}>{spotlight.description}</div>
                </SpotlightCard>
            )}
        </div>
    );
};
